import { useEffect, useRef, useState } from 'react';
import { authApi, type PresetAvatar } from '../../api/auth';
import { useAuthStore } from '../../stores/authStore';

interface AvatarPickerProps {
  onClose: () => void;
}

type Tab = 'upload' | 'gallery';

const MAX_SIZE_MB = 2;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

export default function AvatarPicker({ onClose }: AvatarPickerProps) {
  const [tab, setTab] = useState<Tab>('gallery');
  const [presets, setPresets] = useState<PresetAvatar[]>([]);
  const [preview, setPreview] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { uploadAvatar, selectPresetAvatar } = useAuthStore();

  useEffect(() => {
    authApi.listPresetAvatars().then((res) => setPresets(res.data.presets));
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadError(null);

    if (!ALLOWED_TYPES.includes(file.type)) {
      setUploadError('Imagem deve ser JPEG, PNG ou WebP.');
      return;
    }
    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      setUploadError(`Imagem deve ter no máximo ${MAX_SIZE_MB} MB.`);
      return;
    }

    setSelectedFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setPreview(ev.target?.result as string);
    reader.readAsDataURL(file);
  };

  const handleUploadSave = async () => {
    if (!selectedFile) return;
    setSaving(true);
    try {
      await uploadAvatar(selectedFile);
      onClose();
    } catch {
      setUploadError('Erro ao salvar avatar. Tente novamente.');
    } finally {
      setSaving(false);
    }
  };

  const handlePresetSelect = async (presetName: string) => {
    setSaving(true);
    try {
      await selectPresetAvatar(presetName);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-[#13131A] border border-white/10 rounded-2xl w-full max-w-md mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <h2 className="text-white font-semibold text-lg">Alterar avatar</h2>
          <button onClick={onClose} className="text-white/50 hover:text-white transition-colors cursor-pointer">
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-white/10">
          {(['gallery', 'upload'] as Tab[]).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`flex-1 py-3 text-sm font-medium transition-colors cursor-pointer ${
                tab === t
                  ? 'text-white border-b-2 border-[#C8A96E]'
                  : 'text-white/50 hover:text-white/80'
              }`}
            >
              {t === 'gallery' ? 'Galeria' : 'Upload'}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="p-6">
          {tab === 'gallery' && (
            <div className="grid grid-cols-4 gap-3">
              {presets.map((preset) => (
                <button
                  key={preset.name}
                  onClick={() => handlePresetSelect(preset.name)}
                  disabled={saving}
                  className="aspect-square rounded-xl overflow-hidden border-2 border-transparent hover:border-[#C8A96E] transition-all cursor-pointer disabled:opacity-50"
                >
                  <img
                    src={preset.url}
                    alt={preset.name}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          )}

          {tab === 'upload' && (
            <div className="space-y-4">
              {/* Preview */}
              <div
                className="w-32 h-32 mx-auto rounded-full border-2 border-dashed border-white/20 flex items-center justify-center overflow-hidden cursor-pointer hover:border-[#C8A96E] transition-colors"
                onClick={() => fileInputRef.current?.click()}
              >
                {preview ? (
                  <img src={preview} alt="Preview" className="w-full h-full object-cover" />
                ) : (
                  <div className="text-center">
                    <i className="ri-upload-cloud-2-line text-2xl text-white/30" />
                    <p className="text-white/30 text-xs mt-1">Clique para escolher</p>
                  </div>
                )}
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={handleFileChange}
              />

              <p className="text-white/40 text-xs text-center">
                JPEG, PNG ou WebP · máx. 2 MB · redimensionado para 256×256 px
              </p>

              {uploadError && (
                <p className="text-red-400 text-sm text-center">{uploadError}</p>
              )}

              <div className="flex gap-3">
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="flex-1 border border-white/20 text-white/70 hover:text-white text-sm py-2 rounded-lg transition-colors cursor-pointer"
                >
                  Escolher arquivo
                </button>
                <button
                  onClick={handleUploadSave}
                  disabled={!selectedFile || saving}
                  className="flex-1 bg-[#C8A96E] text-[#0A0A0F] font-semibold text-sm py-2 rounded-lg hover:bg-[#C8A96E]/90 transition-colors disabled:opacity-40 cursor-pointer disabled:cursor-not-allowed"
                >
                  {saving ? 'Salvando…' : 'Salvar'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
